import { currentLanguage, type Language } from '../i18n';

const TZ = 'Europe/Bratislava';

function locale(lang: Language): string {
  return lang === 'sk' ? 'sk-SK' : 'en-GB';
}

/** ISO date (YYYY-MM-DD) → "1. 10. 2026" (sk) / "1 October 2026" (en). */
export function formatDate(iso: string | null | undefined, lang: Language = currentLanguage()): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return new Intl.DateTimeFormat(locale(lang), {
    day: 'numeric',
    month: lang === 'sk' ? 'numeric' : 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/** UTC timestamp → local date and time in Europe/Bratislava. */
export function formatDateTime(isoInstant: string | null | undefined, lang: Language = currentLanguage()): string {
  if (!isoInstant) return '';
  const date = new Date(isoInstant);
  if (Number.isNaN(date.getTime())) return isoInstant;
  return new Intl.DateTimeFormat(locale(lang), {
    day: 'numeric',
    month: lang === 'sk' ? 'numeric' : 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  }).format(date);
}

export function formatTime(isoInstant: string, lang: Language = currentLanguage()): string {
  return new Intl.DateTimeFormat(locale(lang), { hour: '2-digit', minute: '2-digit', timeZone: TZ }).format(
    new Date(isoInstant),
  );
}

export function formatNumber(value: number, lang: Language = currentLanguage()): string {
  return new Intl.NumberFormat(locale(lang)).format(value);
}

/** Today's date in Europe/Bratislava as ISO YYYY-MM-DD. */
export function todayIso(): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date())
    .reduce<Record<string, string>>((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** "01001" → "010 01". */
export function formatPsc(psc: string | null | undefined): string {
  if (!psc) return '';
  const digits = psc.replace(/\s/g, '');
  return digits.length === 5 ? `${digits.slice(0, 3)} ${digits.slice(3)}` : psc;
}
